/**
 * scoring.js
 *
 * Final scores and standings for an Auction game.
 *
 * Teams buy between min and max items, then submit N of them (N = items to submit).
 * Teams with extra items choose their submission (team.submission) at the end. Otherwise it's picked for them:
 * the N items that meet the rules and score the most, bonuses included.
 *
 * A team's total = base score + bonuses, both from the submitted items
 *  -   Base score  :   the sum of the submitted items' scores
 *  -   Bonuses     :   per category attribute (e.g. Actor):
 *                      'all one value' if every submitted item shares one value,
 *                      'all different' if the submission has as many different values as it can:
 *                      all N different, or every value when there are fewer values than N
 * A team that doesn't meet the rules (min items bought, min different values) is ranked
 * below every team that does, whatever its score.
 */

import { getCategoryAttributes, getTeamItems } from "../library/libraries";
import { getTeamColor, sortTeams } from "../assets/teamColors";

// Above this many possible submissions, the best N by score are submitted instead of trying them all
const MAX_SUBMISSIONS_TO_TRY = 20000;

/**
 * How many different values a submission needs for the 'all different' bonus
 * @param {Object} settings     -   Room settings
 * @param {Object} attribute    -   { name, values }
 * @returns {Number}            -   All N different, or every value if there are fewer
 */
export const getAllDifferentTarget = (settings, attribute) => Math.min(settings.itemsToSubmit, attribute.values.length);

/**
 * Every way to pick `size` of the items (as index lists)
 */
const combinations = (count, size) => {
    const result = [];
    const pick = (start, chosen) => {
        if (chosen.length === size) return result.push([...chosen]);
        for (let index = start; index <= count - (size - chosen.length); index++) {
            chosen.push(index);
            pick(index + 1, chosen);
            chosen.pop();
        }
    };
    pick(0, []);
    return result;
};

const countCombinations = (count, size) => {
    let total = 1;
    for (let k = 1; k <= size; k++) total = (total * (count - size + k)) / k;
    return total;
};

/**
 * Score, bonuses and rule problems of one submission
 */
const scoreSubmission = (submitted, settings, categoryAttributes) => {
    const baseScore = submitted.reduce((sum, item) => sum + (Number(item.score) || 0), 0);
    const problems = [];
    const bonuses = [];
    let bonus = 0;

    categoryAttributes.forEach((attribute) => {
        const rule = settings.attributeRules[attribute.name];
        if (!rule) return;
        const values = submitted.map((item) => item.values?.[attribute.name]).filter((value) => value !== undefined && value !== "");
        const different = new Set(values).size;

        if (rule.minDifferent > 0 && different < rule.minDifferent)
            problems.push(`Needs ${rule.minDifferent} different ${attribute.name} values in the ${settings.itemsToSubmit} submitted, has ${different}`);
        if (rule.bonusOneValue > 0 && submitted.length > 0 && values.length === submitted.length && different === 1) {
            bonus += rule.bonusOneValue;
            bonuses.push(`All one ${attribute.name} +${rule.bonusOneValue}`);
        }
        const target = getAllDifferentTarget(settings, attribute);
        if (rule.bonusEveryValue > 0 && target > 0 && different >= target) {
            bonus += rule.bonusEveryValue;
            bonuses.push(`${target === attribute.values.length ? "Every" : "All different"} ${attribute.name} +${rule.bonusEveryValue}`);
        }
    });

    return { baseScore, bonus, bonuses, total: baseScore + bonus, problems };
};

/**
 * The team's own choice of items to submit, if it made a valid one
 * @param {Object} team     -   Team
 * @param {Array} items     -   Its items (see getTeamItems)
 * @param {Number} size     -   How many it submits
 * @returns {Array|null}    -   Item positions, or null to pick the best set for it
 */
const getChosenSubmission = (team, items, size) => {
    const chosen = team.submission;
    if (!Array.isArray(chosen) || chosen.length !== size || new Set(chosen).size !== size) return null;
    return chosen.every((index) => Number.isInteger(index) && index >= 0 && index < items.length) ? chosen : null;
};

/**
 * Score of a team's best submission, for previews (e.g. the choice a team is about to make)
 * @param {Array} submitted         -   Items being submitted
 * @param {Object} settings         -   Room settings
 * @param {Object} library          -   Room library
 * @returns {Object}                -   { baseScore, bonus, bonuses, total, problems }
 */
export const scoreItems = (submitted, settings, library) => scoreSubmission(submitted, settings, getCategoryAttributes(library));

/**
 * Works out one team's score
 * @param {Object} team     -   Team
 * @param {Object} settings -   Room settings (see getAuctionSettings)
 * @param {Object} library  -   Room library
 * @returns {Object}        -   { baseScore, bonus, bonuses: [label], total, metRules, problems: [text], items, submitted, submittedIndexes, chosenByTeam, spent }
 */
export const scoreTeam = (team, settings, library) => {
    const items = getTeamItems(team);
    const categoryAttributes = getCategoryAttributes(library);
    const size = Math.min(settings.itemsToSubmit, items.length);

    const buyingProblems = [];
    if (items.length < settings.minItemsPerTeam)
        buyingProblems.push(`Needs to buy ${settings.minItemsPerTeam} items, has ${items.length}`);
    if (items.length < settings.itemsToSubmit && settings.itemsToSubmit > settings.minItemsPerTeam)
        buyingProblems.push(`Needs ${settings.itemsToSubmit} items to submit, has ${items.length}`);

    // The team's own choice; otherwise try every possible submission (teams only buy a handful of items),
    // or the best N by score if there are too many
    const chosen = getChosenSubmission(team, items, size);
    const byScore = items.map((_, index) => index).sort((a, b) => (Number(items[b].score) || 0) - (Number(items[a].score) || 0));
    const candidates = chosen
        ? [chosen]
        : countCombinations(items.length, size) <= MAX_SUBMISSIONS_TO_TRY
            ? combinations(items.length, size)
            : [byScore.slice(0, size)];

    let best = null;
    for (const indices of candidates) {
        const submitted = indices.map((index) => items[index]);
        const result = { ...scoreSubmission(submitted, settings, categoryAttributes), submitted, indices };
        const better = !best
            || (result.problems.length === 0) > (best.problems.length === 0)
            || ((result.problems.length === 0) === (best.problems.length === 0) && result.total > best.total);
        if (better) best = result;
    }

    const problems = [...buyingProblems, ...best.problems];
    return {
        baseScore   :   best.baseScore,
        bonus       :   best.bonus,
        bonuses     :   best.bonuses,
        total       :   best.total,
        metRules    :   problems.length === 0,
        problems,
        items,
        submitted   :   best.submitted,
        submittedIndexes:   best.indices,
        chosenByTeam    :   !!chosen,
        spent       :   (team.budget ?? settings.budgetPerTeam) - (team.budgetLeft ?? 0),
    };
};

/**
 * Final standings of the teams that played, best first
 * @param {Array} teams     -   Teams in the room
 * @param {Object} settings -   Room settings
 * @param {Object} library  -   Room library
 * @returns {Array}         -   [{ rank, team, color, ...scoreTeam() }]
 */
export const rankTeams = (teams, settings, library) => {
    const scored = sortTeams(teams)
        .filter((team) => team.members?.length > 0)
        .map((team) => ({ team, color: getTeamColor(team), ...scoreTeam(team, settings, library) }))
        .sort((a, b) => (b.metRules - a.metRules) || (b.total - a.total) || (a.spent - b.spent));

    // Teams with the same standing share a rank (e.g. 1, 1, 3)
    const standing = (entry) => `${entry.metRules}|${entry.total}|${entry.spent}`;
    return scored.map((entry) => ({
        ...entry,
        rank: scored.findIndex((other) => standing(other) === standing(entry)) + 1,
    }));
};
